import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Home, Cookie, Shield, Settings, Eye, Phone } from 'lucide-react';

const LOGOS = {
  orange: "/images/logos/orange.svg"
};

const CookiePage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Same style as HomePage */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-3">
              <div className="text-xl font-bold text-blue-600">Telecomprijsvergelijker<span className="text-orange-500">.be</span></div>
            </Link>
            <div className="flex items-center gap-4">
              <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-full">
                <img src={LOGOS.orange} alt="Orange" className="h-5" />
                <span className="text-xs font-medium text-orange-700">Officieel Partner</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Cookie className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-900 mb-4">Cookieverklaring</h1>
          <p className="text-lg text-slate-600">
            Hoe we cookies gebruiken op telecomprijsvergelijker.be
          </p>
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Wat zijn cookies?</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700">
                Cookies zijn kleine tekstbestanden die bij het bezoeken van een website op uw toestel worden geplaatst.
                Ze helpen websites om informatie over uw bezoek te onthouden en uw ervaring te verbeteren.
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                1. Essentiële cookies (verplicht)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">Deze zijn nodig voor:</p>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  De werking van formulieren
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Beveiliging (CSRF, sessies)
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  Navigatie
                </li>
              </ul>
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg mt-4">
                <p className="text-green-800 text-sm">Deze cookies kunnen niet worden uitgeschakeld - ze zijn essentieel voor de werking.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Eye className="w-5 h-5 text-blue-600" />
                2. Analytische cookies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">Wij kunnen analytische tools gebruiken om het gebruik van de website te analyseren en prestaties te verbeteren, zoals Google Analytics (geanonimiseerd).</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                3. Marketingcookies
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">We kunnen adverteren via Facebook (Meta) en Google Ads. Deze cookies worden enkel geplaatst na uw toestemming.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Cookies beheren of verwijderen</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">U kan cookies op elk moment verwijderen of blokkeren via uw browserinstellingen.</p>
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
                <p className="text-yellow-800 text-sm">⚠️ Let op: Sommige functies kunnen hierdoor minder goed werken.</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Contact</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 mb-4">Vragen over cookies? Neem contact op via ons contactformulier.</p>
              <div className="flex gap-4 mt-6">
                <Button onClick={() => navigate('/contact')}>Contactformulier</Button>
                <Button variant="outline" onClick={() => navigate('/')}>Terug naar home</Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer - Same style as HomePage */}
      <footer className="bg-slate-900 text-slate-400 py-12 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <div className="text-xl font-bold text-white mb-4">
                Telecomprijsvergelijker<span className="text-orange-500">.be</span>
              </div>
              <p className="text-sm mb-4">
                Wij helpen u besparen op TV, internet en mobiel door Telenet, Proximus en Base te vergelijken met Orange's aanbod.
              </p>
              <div className="flex items-center gap-2 bg-slate-800 rounded-lg px-3 py-2 inline-flex">
                <img src={LOGOS.orange} alt="Orange" className="h-6" />
                <span className="text-sm text-white font-medium">Officieel Partner</span>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacybeleid</Link></li>
                <li><span className="text-white">Cookiebeleid</span></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/contact" className="hover:text-white transition-colors flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Contactformulier
                  </Link>
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm">
            <p className="mb-2">Officieel Orange partner via distributiekanaal SmartSN</p>
            <p>© {new Date().getFullYear()} Telecomprijsvergelijker.be - Alle rechten voorbehouden</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CookiePage;
